import type { ThemeConfig } from 'antd';

export type SemanticTone = 'success' | 'info' | 'warning' | 'error' | 'neutral';

export const semanticColors: Record<SemanticTone, { base: string; text: string; background: string; border: string }> = {
  success: { base: '#15805f', text: '#147055', background: '#e7f5f0', border: '#b7ded1' },
  info: { base: '#176b87', text: '#125c75', background: '#e5f1f4', border: '#b9d4dc' },
  warning: { base: '#b57311', text: '#8a5a10', background: '#fff3df', border: '#efd39c' },
  error: { base: '#b63838', text: '#b63838', background: '#fbe9e9', border: '#efc3c3' },
  neutral: { base: '#596675', text: '#53616e', background: '#f1f3f5', border: '#d9dfe3' },
};

export const severityColors = {
  high: { base: '#c45f25', text: '#a84f1f', background: '#fff0e8', border: '#efc3a8' },
} as const;

export const designTokens = {
  color: {
    primary: '#176b87',
    info: '#176b87',
    success: semanticColors.success.base,
    warning: semanticColors.warning.base,
    error: semanticColors.error.base,
    text: '#17212b',
    textSecondary: '#596675',
    layout: '#f3f6f8',
    container: '#ffffff',
    secondary: '#f5f7f9',
    border: '#dfe7ea',
    divider: '#e3e9ec',
    tableHeader: '#f5f7f9',
    hover: '#f3f7f8',
    selected: '#e9f2f4',
    selectedHover: '#e1edef',
    sider: '#0e2538',
    accent: '#d8a43f',
  },
  space: { xs: 8, sm: 12, md: 16, lg: 20, xl: 24 },
  radius: { control: 7, inner: 8, container: 9, overlay: 12 },
  controlHeight: 32,
  emphasizedControlHeight: 40,
  tableRowHeight: 36,
  zIndex: { content: 1, sticky: 10, tabs: 21, header: 22, scrim: 25, sider: 30 },
} as const;

export const overlaySizes = {
  confirmModal: 480,
  selectionModal: 680,
  detailDrawer: 620,
  formDrawer: 760,
} as const;

export const chartTheme = {
  colors: [designTokens.color.primary, designTokens.color.warning],
  statusColors: [designTokens.color.success, designTokens.color.info, designTokens.color.error, semanticColors.neutral.base],
  axis: '#d9e0e5',
  splitLine: '#edf1f3',
  axisLabel: '#697785',
  grid: { top: 44, left: 16, right: 24, bottom: 12, containLabel: true },
} as const;

export const appTheme: ThemeConfig = {
  token: {
    colorPrimary: designTokens.color.primary,
    colorInfo: designTokens.color.info,
    colorSuccess: designTokens.color.success,
    colorWarning: designTokens.color.warning,
    colorError: designTokens.color.error,
    colorText: designTokens.color.text,
    colorTextSecondary: designTokens.color.textSecondary,
    colorBgLayout: designTokens.color.layout,
    colorBgContainer: designTokens.color.container,
    colorBorderSecondary: designTokens.color.border,
    borderRadius: designTokens.radius.control,
    borderRadiusLG: designTokens.radius.container,
    fontFamily: "'Inter', 'Noto Sans TC', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    controlHeight: designTokens.controlHeight,
    fontSize: 14,
  },
  components: {
    Layout: { headerBg: designTokens.color.container, siderBg: designTokens.color.sider },
    Menu: { darkItemBg: designTokens.color.sider, darkSubMenuItemBg: designTokens.color.sider, itemHeight: 38 },
    Table: {
      headerBg: designTokens.color.tableHeader,
      headerColor: '#435260',
      headerSplitColor: 'transparent',
      rowHoverBg: designTokens.color.hover,
      rowSelectedBg: designTokens.color.selected,
      rowSelectedHoverBg: designTokens.color.selectedHover,
      cellPaddingBlock: 8,
      cellPaddingBlockSM: 5,
      cellPaddingInlineSM: 8,
    },
    Button: { fontWeight: 600 },
    Card: { paddingLG: designTokens.space.lg },
    Drawer: { borderRadiusLG: designTokens.radius.overlay },
    Modal: { borderRadiusLG: designTokens.radius.overlay },
  },
};
